import * as XLSX from "xlsx";
import logger from "../logger/logger";

/**
 * Service for downloading and processing Excel files with frozen formulas
 */
class ExcelDownloadService {
  /**
   * Download Excel file from S3, freeze formulas, and trigger download
   * @param s3Url The S3 URL of the Excel file
   * @param filename Optional filename for the download
   */
  async downloadAndProcessExcelFile(s3Url: string, filename?: string): Promise<void> {
    try {
      // Fetch the Excel file from S3
      const response = await fetch(s3Url);
      if (!response.ok) {
        throw new Error(`Failed to fetch Excel file: ${response.status} ${response.statusText}`);
      }

      // Get the file as ArrayBuffer
      const arrayBuffer = await response.arrayBuffer();

      // Parse the Excel file with all styling preserved
      const workbook = XLSX.read(arrayBuffer, {
        type: "array",
        cellDates: true,
        cellStyles: true,
        cellNF: true,
        WTF: false,
      });

      // Process each sheet to freeze formulas
      const processedWorkbook = this.freezeFormulasInWorkbook(workbook);

      // Convert back to Excel format (preserving all styling and formatting)
      const excelBuffer = XLSX.write(processedWorkbook, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true, // Preserve cell styles
        compression: true,
      });

      // Create blob and trigger download
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Generate filename if not provided
      const downloadFilename =
        filename ||
        `SmartBEA_Data_Collection_${new Date().getFullYear()}_${String(new Date().getMonth() + 1).padStart(
          2,
          "0"
        )}.xlsx`;

      // Trigger download
      this.triggerDownload(blob, downloadFilename);
    } catch (error) {
      logger.error("Error downloading and processing Excel file:", error);
      throw new Error(`Failed to process Excel file: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Freeze formulas in all sheets of a workbook
   * @param workbook The workbook to process
   * @returns Processed workbook with frozen formulas
   */
  private freezeFormulasInWorkbook(workbook: XLSX.WorkBook): XLSX.WorkBook {
    // Create a copy of the workbook preserving all properties
    const processedWorkbook: XLSX.WorkBook = {
      SheetNames: [...workbook.SheetNames],
      Sheets: {},
      Props: workbook.Props,
      Custprops: workbook.Custprops,
      Workbook: workbook.Workbook, // Preserve workbook-level settings
      vbaraw: workbook.vbaraw, // Preserve VBA if present
    };

    // Process each sheet
    workbook.SheetNames.forEach((sheetName) => {
      const originalSheet = workbook.Sheets[sheetName];
      const processedSheet = this.freezeFormulasInSheet(originalSheet);
      processedWorkbook.Sheets[sheetName] = processedSheet;
    });

    return processedWorkbook;
  }

  /**
   * Freeze formulas in a single sheet
   * @param sheet The sheet to process
   * @returns Processed sheet with frozen formulas
   */
  private freezeFormulasInSheet(sheet: XLSX.WorkSheet): XLSX.WorkSheet {
    // Create a deep copy of the sheet to preserve all properties
    const processedSheet: XLSX.WorkSheet = JSON.parse(JSON.stringify(sheet));

    // Get the range of the sheet
    const range = XLSX.utils.decode_range(sheet["!ref"] || "A1:A1");

    // Track the starting row for date progression
    let dateStartRow = -1;
    let baseDate: Date | null = null;

    // Process each cell in the sheet
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = sheet[cellAddress];

        if (cell && cell.f) {
          // This cell has a formula
          let frozenValue: any;

          // Check if this is a date formula and handle sequential progression
          if (this.isDateFormula(cell.f)) {
            if (dateStartRow === -1) {
              // This is the first date formula - calculate the base date
              baseDate = this.evaluateFormula(cell.f);
              dateStartRow = row;
              frozenValue = baseDate;
            } else {
              // This is a subsequent date formula - increment from base date
              const monthsToAdd = row - dateStartRow;
              if (baseDate) {
                frozenValue = new Date(baseDate.getFullYear(), baseDate.getMonth() + monthsToAdd, 1);
              } else {
                frozenValue = this.evaluateFormula(cell.f);
              }
            }
          } else {
            // Not a date formula, evaluate normally
            frozenValue = this.evaluateFormula(cell.f);
          }

          // Create a new cell with the frozen value while preserving all styling
          const newCell: any = {
            ...cell, // Preserve all original cell properties (styling, formatting, etc.)
            v: frozenValue, // Set the value
            f: undefined, // Remove the formula
            t: this.getValueType(frozenValue), // Set the correct type
          };

          // If it's a date, ensure proper Excel date handling
          if (frozenValue instanceof Date) {
            // Convert to Excel date serial number
            const excelSerial = this.dateToExcelSerial(frozenValue);
            newCell.v = excelSerial;
            newCell.t = "n"; // Number type for Excel dates

            // Preserve original number format if it exists, otherwise set mm/dd/yy
            if (!newCell.z) {
              newCell.z = "mm/dd/yy";
            }

            // Set display text for Month, Year format
            newCell.w = this.formatDateAsMonthYear(frozenValue);
          }

          processedSheet[cellAddress] = newCell;
        }
      }
    }

    return processedSheet;
  }

  /**
   * Convert JavaScript Date to Excel serial number
   * @param date The JavaScript Date
   * @returns Excel serial number
   */
  private dateToExcelSerial(date: Date): number {
    // Excel epoch starts from January 1, 1900 (with leap year bug)
    const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
    const timeDiff = date.getTime() - excelEpoch.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    return daysDiff;
  }

  /**
   * Check if a formula is a date-related formula
   * @param formula The formula to check
   * @returns True if it's a date formula
   */
  private isDateFormula(formula: string): boolean {
    const upperFormula = formula.toUpperCase();
    return (
      upperFormula.includes("EDATE") ||
      upperFormula.includes("TODAY") ||
      upperFormula.includes("NOW") ||
      upperFormula.includes("DATE")
    );
  }

  /**
   * Format date as "Month, Year" display text
   * @param date The date to format
   * @returns Formatted string like "May, 2023"
   */
  private formatDateAsMonthYear(date: Date): string {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${month}, ${year}`;
  }

  /**
   * Evaluate a formula and return the result
   * @param formula The formula to evaluate
   * @returns The evaluated value
   */
  private evaluateFormula(formula: string): any {
    try {
      // Handle common Excel functions that might be in the template
      if (formula.toUpperCase().includes("EDATE")) {
        return this.evaluateEDATE(formula);
      }
      if (formula.toUpperCase().includes("TODAY")) {
        return this.evaluateTODAY();
      }
      if (formula.toUpperCase().includes("NOW")) {
        return new Date();
      }
      if (formula.toUpperCase().includes("DATE")) {
        return this.evaluateDATE(formula);
      }

      // For other formulas, try to return a reasonable default or the original formula
      logger.warn(`Unsupported formula: ${formula}, returning original formula text`);
      return `=${formula}`;
    } catch (error) {
      logger.warn(`Error evaluating formula: ${formula}`, error);
      return `=${formula}`;
    }
  }

  /**
   * Evaluate EDATE function
   * @param formula The EDATE formula
   * @returns The calculated date
   */
  private evaluateEDATE(formula: string): Date {
    // Handle EDATE(TODAY(), -25) and similar patterns
    const edateMatch = formula.match(/EDATE\s*\(\s*TODAY\s*\(\s*\)\s*,\s*(-?\d+)\s*\)/i);
    if (edateMatch) {
      const months = parseInt(edateMatch[1], 10);
      const today = new Date();

      // Calculate the result date - go to the 1st day of the target month
      const resultDate = new Date(today.getFullYear(), today.getMonth() + months, 1);

      return resultDate;
    }

    // Handle EDATE with specific date
    const edateDateMatch = formula.match(
      /EDATE\s*\(\s*DATE\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)\s*,\s*(-?\d+)\s*\)/i
    );
    if (edateDateMatch) {
      const year = parseInt(edateDateMatch[1], 10);
      const month = parseInt(edateDateMatch[2], 10) - 1; // JavaScript months are 0-indexed
      const day = parseInt(edateDateMatch[3], 10);
      const addMonths = parseInt(edateDateMatch[4], 10);

      const baseDate = new Date(year, month, day);
      const result = new Date(baseDate.getFullYear(), baseDate.getMonth() + addMonths, 1);
      return result;
    }

    // Handle other EDATE patterns
    const generalEdateMatch = formula.match(/EDATE\s*\(/i);
    if (generalEdateMatch) {
      // For the specific case mentioned: EDATE(TODAY(), -25)
      // Calculate 25 months ago from today, set to 1st of month
      const today = new Date();
      const result = new Date(today.getFullYear(), today.getMonth() - 25, 1);
      return result;
    }

    // Default fallback
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() - 25, 1);
  }

  /**
   * Evaluate TODAY function
   * @returns Today's date
   */
  private evaluateTODAY(): Date {
    return new Date();
  }

  /**
   * Evaluate DATE function
   * @param formula The DATE formula
   * @returns The calculated date
   */
  private evaluateDATE(formula: string): Date {
    const dateMatch = formula.match(/DATE\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
    if (dateMatch) {
      const year = parseInt(dateMatch[1], 10);
      const month = parseInt(dateMatch[2], 10) - 1; // JavaScript months are 0-indexed
      const day = parseInt(dateMatch[3], 10);
      return new Date(year, month, day);
    }

    // Default fallback
    return new Date();
  }

  /**
   * Get the appropriate cell type for a value
   * @param value The value to type
   * @returns The cell type
   */
  private getValueType(value: any): string {
    if (value instanceof Date) {
      return "n"; // Dates are stored as numbers in Excel
    }
    if (typeof value === "number") {
      return "n";
    }
    if (typeof value === "boolean") {
      return "b";
    }
    return "s"; // string
  }

  /**
   * Trigger file download in the browser
   * @param blob The file blob
   * @param filename The filename
   */
  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL
    URL.revokeObjectURL(url);
  }
}

export default new ExcelDownloadService();
